/**
 * K108-W — GLOBAL return-to-OptiBay training completion prompt.
 *
 * The completion modal previously only fired on the Passport foundational tab (and
 * later the Ticket Training tab). This app-level provider watches for the user
 * RETURNING to OptiBay (window focus / tab visible / in-app navigation) and, when
 * the latest pending training session has monitored evidence and no attestation
 * yet, auto-opens TrainingConsumptionConfirmDialog ONCE for that session — no need
 * to click back into any specific section. It also exposes the candidate +
 * openPrompt() so surfaces (Ticket Training tab, Learning History) can show a
 * persistent "Record completion" CTA that survives a dismiss.
 *
 * Uses the corrected K108-V engagement summary (active/duration). Read-only; no DB
 * change. Never blocks; degrades to a null candidate.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';
import {
  selectTicketCompletionCandidate,
  type TicketCompletionCandidate,
} from '@/hooks/useTicketTrainingCompletionPrompt';
import { TrainingConsumptionConfirmDialog } from '@/components/training/TrainingConsumptionConfirmDialog';
// Phase Q1 — offer the optional knowledge check after a desktop/Companion completion.
import { TrainingQuizModal } from '@/components/training/TrainingQuizModal';
import { useTrainingQuizLauncher } from '@/hooks/useTrainingQuizLauncher';

// Auto-open only once the session shows MEANINGFUL engagement — either it has
// terminally stopped (provider tab closed) or enough watch time — so a just-
// launched session (0s watched) does not immediately re-prompt.
const AUTO_OPEN_MIN_ACTIVE_SEC = 30;
const AUTO_OPENED_KEY = 'optibay_tc_autoopened';

interface ReturnPromptValue {
  candidate: TicketCompletionCandidate | null;
  openPrompt: () => void;
}

const Ctx = createContext<ReturnPromptValue | null>(null);

/**
 * PURE: is this candidate's session eligible to AUTO-OPEN the modal? Only once it
 * shows meaningful monitored engagement — terminally stopped (provider tab closed)
 * OR at least AUTO_OPEN_MIN_ACTIVE_SEC watched — so a just-launched session (0s
 * watched, no evidence) never immediately re-prompts. Exported for tests.
 */
export function isAutoOpenEligible(
  summary: { evidenceUnavailable: boolean; stopped: boolean; activeSeconds: number | null } | null | undefined,
): boolean {
  if (!summary || summary.evidenceUnavailable) return false;
  return summary.stopped || (summary.activeSeconds ?? 0) >= AUTO_OPEN_MIN_ACTIVE_SEC;
}

/** Surfaces read this to show a "Record completion" CTA. Null outside the provider. */
export function useTrainingCompletionReturnPrompt(): ReturnPromptValue | null {
  return useContext(Ctx);
}

function autoOpenedSet(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(AUTO_OPENED_KEY) || '[]') as string[]);
  } catch {
    return new Set();
  }
}
function markAutoOpened(id: string): void {
  try {
    const s = autoOpenedSet();
    s.add(id);
    sessionStorage.setItem(AUTO_OPENED_KEY, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
}

export function TrainingCompletionReturnPromptProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [technicianProfileId, setTechnicianProfileId] = useState<string | null>(null);

  // Resolve the current technician profile (mirrors the consent provider).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        setTechnicianProfileId(null);
        return;
      }
      try {
        const { data } = await supabase
          .from('technician_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (!cancelled) setTechnicianProfileId((data as { id?: string } | null)?.id ?? null);
      } catch {
        if (!cancelled) setTechnicianProfileId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const sessionsQuery = useQuery({
    queryKey: ['training-completion-return-prompt', technicianProfileId],
    enabled: !!technicianProfileId,
    staleTime: 10 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, technician_profile_id, artifact_id, ticket_id, metadata, completion_status, opened_at, artifact:training_artifacts(id, title, artifact_type, provider_name)',
        )
        .eq('technician_profile_id', technicianProfileId as string)
        // K108-Y: include 'partial' too — a RESUMED partial session with new
        // evidence should prompt the tech to UPDATE their report.
        .in('completion_status', ['pending', 'partial'])
        .order('opened_at', { ascending: false })
        .limit(10);
      if (error) return [];
      return data ?? [];
    },
  });

  const refetch = sessionsQuery.refetch;
  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);
  const sessionIds = useMemo(() => sessions.map((s) => s.id as string), [sessions]);
  const { data: summaries } = useTrainingSessionMonitoringSummary({
    sessionIds,
    enabled: sessionIds.length > 0,
  });

  // The completion candidate INCLUDES pending sessions with NO monitored evidence,
  // so returning to OptiBay auto-opens the dialog whether or not Companion posted
  // evidence. Evidence only affects the confidence/verification LABEL inside the
  // dialog — never whether the dialog appears or whether the user can report done.
  // Also drives the manual "Record completion" CTA + openPrompt().
  const candidate = useMemo(
    () => selectTicketCompletionCandidate(sessions as never, summaries, { allowNoEvidence: true }),
    [sessions, summaries],
  );

  const [open, setOpen] = useState(false);
  const [modalSession, setModalSession] = useState<TicketCompletionCandidate['session'] | null>(null);
  const [modalIsUpdate, setModalIsUpdate] = useState(false);

  const openPrompt = useCallback(() => {
    if (!candidate) return;
    setModalSession(candidate.session);
    setModalIsUpdate(candidate.isUpdate);
    setOpen(true);
  }, [candidate]);

  // RETURN detection. A genuine return = the tab was hidden/blurred (the user went
  // to the training/provider) and then became visible/focused again. We auto-open
  // ONLY on such a return — never on first load or right after launch — so the
  // dialog never pops before the user has actually left and come back.
  const [returnPending, setReturnPending] = useState(false);
  const awayRef = useRef(false);
  useEffect(() => {
    if (!technicianProfileId) return;
    const markAway = () => {
      awayRef.current = true;
    };
    const markReturn = () => {
      if (awayRef.current) {
        awayRef.current = false;
        setReturnPending(true);
      }
      void refetch();
    };
    const onVisible = () => {
      if (document.visibilityState === 'hidden') markAway();
      else markReturn();
    };
    window.addEventListener('blur', markAway);
    window.addEventListener('focus', markReturn);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('blur', markAway);
      window.removeEventListener('focus', markReturn);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [technicianProfileId, refetch]);

  // In-app navigation keeps the candidate fresh (does NOT count as a return).
  useEffect(() => {
    if (technicianProfileId) void refetch();
  }, [location.pathname, technicianProfileId, refetch]);

  // AUTO-OPEN on a genuine RETURN to OptiBay — EVIDENCE-INDEPENDENT (no active-
  // seconds threshold, no tab-closed requirement) per the product rule. One-shot:
  // the return is consumed immediately so a later launch never auto-pops without a
  // fresh return. The per-session guard key (sessionId + latest-evidence time) means
  // a dismiss is not re-spammed, while a RESUMED session (new evidence) re-prompts
  // once; the manual "Record completion" CTA remains the backup to reopen.
  // (isAutoOpenEligible is retained/exported for reference but no longer gates this.)
  useEffect(() => {
    if (!returnPending) return;
    setReturnPending(false);
    if (!candidate) return;
    const runKey = `${candidate.session.id}:${candidate.summary.lastActivityAt ?? '0'}`;
    if (autoOpenedSet().has(runKey)) return;
    markAutoOpened(runKey);
    setModalSession(candidate.session);
    setModalIsUpdate(candidate.isUpdate);
    setOpen(true);
  }, [returnPending, candidate]);

  const value = useMemo<ReturnPromptValue>(() => ({ candidate, openPrompt }), [candidate, openPrompt]);

  // Phase Q1 — optional post-completion knowledge check (flag-gated; the launcher
  // no-ops unless trainingQuizMvp is on AND an approved quiz exists for the artifact).
  const quiz = useTrainingQuizLauncher();

  return (
    <Ctx.Provider value={value}>
      {children}
      {modalSession && (
        <TrainingConsumptionConfirmDialog
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            // On close (submit or cancel) re-check: a submitted session leaves
            // pending and clears the candidate/CTA; a cancel keeps it.
            if (!next) void refetch();
          }}
          session={modalSession}
          titleOverride={modalIsUpdate ? 'Update your training report?' : undefined}
          onCompleted={({ artifactId, sessionId }) => void quiz.launch(artifactId, sessionId)}
        />
      )}
      {quiz.target && (
        <TrainingQuizModal
          open
          artifactId={quiz.target.artifactId}
          sessionId={quiz.target.sessionId}
          adapter={quiz.adapter}
          prefetchedQuiz={quiz.target.quiz}
          onClose={quiz.close}
          onResult={quiz.handleResult}
        />
      )}
    </Ctx.Provider>
  );
}
